import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, Mail, Phone } from 'lucide-react';

const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--glass-border)',
    background: 'rgba(255,255,255,0.6)', outline: 'none',
    fontSize: '14px', boxSizing: 'border-box'
};

const Login = () => {
    const [method, setMethod] = useState('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { login, signInWithGoogle, setupRecaptcha, sendPhoneOTP } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/dashboard';

    const handleRedirect = () => navigate(from, { replace: true });

    const handleGoogle = async () => {
        try {
            setError(''); setLoading(true);
            await signInWithGoogle();
            handleRedirect();
        } catch (err) {
            setError('Google sign-in failed. Please try again.');
        } finally { setLoading(false); }
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        try {
            setError(''); setLoading(true);
            await login(email, password);
            handleRedirect();
        } catch (err) {
            setError('Incorrect email or password.');
        } finally { setLoading(false); }
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        try {
            setError(''); setLoading(true);
            const fullPhone = phone.startsWith('+') ? phone : `+233${phone.replace(/^0/, '')}`;
            setupRecaptcha('recaptcha-container');
            const result = await sendPhoneOTP(fullPhone);
            setConfirmationResult(result);
            setOtpSent(true);
        } catch (err) {
            setError('Failed to send OTP. Check the number and try again.');
        } finally { setLoading(false); }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        try {
            setError(''); setLoading(true);
            await confirmationResult.confirm(otp);
            handleRedirect();
        } catch (err) {
            setError('Invalid code. Please try again.');
        } finally { setLoading(false); }
    };

    return (
        <div className="container" style={{ minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div id="recaptcha-container"></div>
            <div className="glass-card" style={{ maxWidth: '380px', width: '100%', padding: '28px' }}>

                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '22px', marginBottom: '4px' }}>Welcome Back</h2>
                    <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>Log in to continue your learning journey</p>
                </div>

                {error && (
                    <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid #e74c3c', color: '#e74c3c', padding: '8px 12px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <AlertCircle size={14} /> {error}
                    </div>
                )}

                {/* Google */}
                <button
                    onClick={handleGoogle}
                    disabled={loading}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginBottom: '16px' }}
                >
                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.5 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7.1l-6.6 4.8C9.7 39.7 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.5-2.6 4.6-4.8 6l6.2 5.2C40.5 35.6 44 30.3 44 24c0-1.3-.1-2.7-.4-4z"/></svg>
                    Continue with Google
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>or</span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
                </div>

                {/* Method Tabs */}
                <div style={{ display: 'flex', background: 'rgba(0,0,0,0.05)', borderRadius: '8px', padding: '3px', marginBottom: '16px' }}>
                    {[{ id: 'email', label: 'Email', icon: <Mail size={13}/> }, { id: 'phone', label: 'Phone', icon: <Phone size={13}/> }].map(tab => (
                        <button key={tab.id} onClick={() => { setMethod(tab.id); setError(''); setOtpSent(false); }}
                            style={{ flex: 1, padding: '7px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', background: method === tab.id ? 'white' : 'transparent', color: method === tab.id ? 'var(--primary)' : 'var(--text-light)', boxShadow: method === tab.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}>
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>

                {/* Email Form */}
                {method === 'email' && (
                    <form onSubmit={handleEmailLogin}>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Email</label>
                            <input type="email" required style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
                        </div>
                        <div style={{ marginBottom: '6px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Password</label>
                            <input type="password" required style={inputStyle} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
                        </div>
                        <div style={{ textAlign: 'right', marginBottom: '16px' }}>
                            <Link to="/forgot-password" style={{ fontSize: '12px', color: 'var(--primary)', textDecoration: 'none' }}>Forgot password?</Link>
                        </div>
                        <button disabled={loading} className="btn btn-primary" style={{ width: '100%', padding: '10px', fontSize: '14px' }}>
                            {loading ? 'Logging in...' : 'Log In'}
                        </button>
                    </form>
                )}

                {/* Phone Form */}
                {method === 'phone' && !otpSent && (
                    <form onSubmit={handleSendOTP}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Phone Number</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.6)', fontSize: '14px', whiteSpace: 'nowrap' }}>🇬🇭 +233</div>
                                <input type="tel" required style={{ ...inputStyle, flex: 1 }} value={phone} onChange={e => setPhone(e.target.value)} placeholder="0244 000 000" />
                            </div>
                        </div>
                        <button disabled={loading} className="btn btn-primary" style={{ width: '100%', padding: '10px', fontSize: '14px' }}>
                            {loading ? 'Sending...' : 'Send OTP'}
                        </button>
                    </form>
                )}

                {method === 'phone' && otpSent && (
                    <form onSubmit={handleVerifyOTP}>
                        <p style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '12px' }}>
                            Code sent to {phone}. <button type="button" onClick={() => setOtpSent(false)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '13px', padding: 0 }}>Change</button>
                        </p>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>6-digit code</label>
                            <input type="text" inputMode="numeric" maxLength={6} required style={{ ...inputStyle, letterSpacing: '6px', fontSize: '20px', textAlign: 'center' }} value={otp} onChange={e => setOtp(e.target.value)} placeholder="——————" />
                        </div>
                        <button disabled={loading} className="btn btn-primary" style={{ width: '100%', padding: '10px', fontSize: '14px' }}>
                            {loading ? 'Verifying...' : 'Verify & Log In'}
                        </button>
                    </form>
                )}

                <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'var(--text-light)' }}>
                    Don't have an account?{' '}
                    <Link to="/signup" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: '500' }}>Sign Up</Link>
                </p>
            </div>
        </div>
    );
};

export default Login;
